import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Card,
    CardContent,
    CircularProgress,
    Grid,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
    getAllMainCategoriesWithQuestionCount,
    getSubcategoriesByMainCategoryWithQuestionCount,
    getCategories,
    addSubcategory,
    addCategory,
    uploadQuestion,
    uploadChoice,
    getReviewedQuestions,
    updateQuestion,
    getConfirmedQuestionsCount,
    getUploadedQuestions,
    getSubcategories, // Added getSubcategories for reviewed tab dropdown
} from "../../../../api/auth";
import AdminBankModeTabs from "./AdminBankModeTabs";

const DashboardQuestionUpload = () => {
    const [searchParams] = useSearchParams();
    const isMockBankMode = searchParams.get("bank") === "mock";

    const [activeTab, setActiveTab] = useState("upload"); // "upload" or "reviewed"
    const { universityName, courseName } = useSelector((state) => state.auth);
    const userId = useSelector((state) => state.auth.userId);

    const [isCategoryMode, setIsCategoryMode] = useState(false); // Toggle for upload or manage categories
    const [isTableViewMode, setIsTableViewMode] = useState(false); // Toggle for table view
    const [mainCategories, setMainCategories] = useState([]);
    const [subCategories, setSubCategories] = useState({}); // Map of cat_id -> [subcategories]
    const [categories, setCategories] = useState([]); // For table display
    const [allSubcategories, setAllSubcategories] = useState([]); // For table and reviewed display
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
    const [questionText, setQuestionText] = useState("");
    const [lectureReference, setLectureReference] = useState("");
    const [questionType, setQuestionType] = useState("");
    const [highYield, setHighYield] = useState(false);

    // State for file upload
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileConfirmation, setFileConfirmation] = useState("");
    const [mainImageFile, setMainImageFile] = useState(null); // Main image file for Part A (optional)
    const [saqImageFiles, setSaqImageFiles] = useState({}); // SAQ per-part image files: map of partIndex -> File

    // Uploaded Questions Table State
    const [uploadedQuestions, setUploadedQuestions] = useState([]);
    const [showUploadedTable, setShowUploadedTable] = useState(false);
    const [uploadFilters, setUploadFilters] = useState({
        university: universityName,
        course: courseName,
        type: "",
        high_yield: "",
        is_confirmed: "",
        cat_id: "",
        subcat_id: "",
    });

    // Question/Choice upload states
    const [choicesText, setChoicesText] = useState(""); // Text for all choices
    const [explanationsText, setExplanationsText] = useState(""); // Text for all explanations
    const [correctChoiceIndex, setCorrectChoiceIndex] = useState(""); // Correct choice index
    const [confirmedCount, setConfirmedCount] = useState(0);
    const [targetCount, setTargetCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [portalMockName, setPortalMockName] = useState("portal_pool");

    // Reviewed Questions States
    const [reviewedQuestions, setReviewedQuestions] = useState([]);
    const [loadingReviewedQuestions, setLoadingReviewedQuestions] = useState(false);
    const [editedQuestions, setEditedQuestions] = useState({});

    // States for adding categories and subcategories (Deduplicated)
    const [categoryName, setCategoryName] = useState("");
    const [categoryYear, setCategoryYear] = useState("");
    const [subcategoryName, setSubcategoryName] = useState("");

    // SAQ parts B/C/D... linked to Part A for UPLOAD form
    const [saqParts, setSaqParts] = useState([]); // [{ letter:'B', question_text:'', correct_answer:'', image:'' }, ...]

    // --- Helper Functions (Deduplicated and Consolidated) ---

    const validateFields = (fields) => {
        return Object.values(fields).every((value) => {
            if (typeof value === "string") {
                return value.trim() !== ""; // Ensure non-empty strings
            }
            return Boolean(value); // Check for truthy values
        });
    };

    const nextLetter = (index) => String.fromCharCode("B".charCodeAt(0) + index);

    const addSaqPart = () => {
        const letter = nextLetter(saqParts.length);
        setSaqParts((prev) => [
            ...prev,
            { letter, question_text: "", correct_answer: "", image: "" },
        ]);
    };

    const removeSaqPart = (i) => {
        setSaqParts((prev) => prev.filter((_, idx) => idx !== i));
    };

    const updateSaqPart = (i, field, value) => {
        setSaqParts((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], [field]: value };
            return copy;
        });
    };

    const buildChildJson = () => {
        const cat = mainCategories.find((c) => c.cat_id === selectedCategoryId);
        const sub = (subCategories[selectedCategoryId] || []).find(
            (s) => s.subcat_id === selectedSubcategoryId
        );
        return saqParts.map(({ letter, question_text, correct_answer, image }) => ({
            [`question_${letter.toLowerCase()}_text`]: question_text,
            category: cat?.cat_name ?? "",
            category_id: selectedCategoryId,
            subcategory: sub?.subcategory_name ?? "",
            subcategory_id: selectedSubcategoryId,
            image: image || "",
            university_name: universityName,
            course_name: courseName,
            correct_answer,
        }));
    };

    // Parse choices and explanations from input
    const parseInput = (text) => {
        return text
            .split("\\")
            .map((item) => item.trim())
            .filter((item) => item);
    };

    const resetForm = () => {
        setQuestionText("");
        setLectureReference("");
        setQuestionType("");
        setHighYield(false);
        setChoicesText("");
        setExplanationsText("");
        setCorrectChoiceIndex("");
        setSaqParts([]);
        setMainImageFile(null);
        setSaqImageFiles({});
        setSelectedFile(null);
        setFileConfirmation("");
    };

    // Handle field changes for reviewed questions
    const handleInputChange = (questionId, field, value) => {
        setEditedQuestions((prev) => ({
            ...prev,
            [questionId]: {
                ...prev[questionId],
                [field]: value,
            },
        }));
    };

    const handleChoiceChange = (questionId, choiceIndex, field, value) => {
        setEditedQuestions((prev) => {
            const updatedChoices = [...(prev[questionId]?.choices || [])];
            updatedChoices[choiceIndex] = {
                ...updatedChoices[choiceIndex],
                [field]: value,
            };

            return {
                ...prev,
                [questionId]: {
                    ...prev[questionId],
                    choices: updatedChoices,
                },
            };
        });
    };

    // --- Data Fetching and Handlers ---

    // Fetch confirmed count for progress bar
    useEffect(() => {
        async function fetchConfirmedCount() {
            try {
                const response = await getConfirmedQuestionsCount(
                    userId,
                    isMockBankMode
                );
                setConfirmedCount(response.confirmedCount);
            } catch (error) {
                console.error("Error fetching confirmed questions count:", error.message);
            } finally {
                setLoading(false);
            }
        }
        fetchConfirmedCount();
    }, [userId, isMockBankMode]);

    // Fetch main categories
    useEffect(() => {
        async function fetchMainCategories() {
            if (universityName && courseName) {
                try {
                    const response = await getAllMainCategoriesWithQuestionCount(
                        universityName,
                        courseName
                    );
                    setMainCategories(response.data.mainCategories);
                } catch (error) {
                    console.error("Error fetching main categories:", error);
                }
            }
        }
        fetchMainCategories();
    }, [universityName, courseName]);

    // Fetch subcategories for the selected category in the UPLOAD/ADD CATEGORY form
    useEffect(() => {
        if (selectedCategoryId) {
            const fetchSubcategories = async () => {
                try {
                    const response =
                        await getSubcategoriesByMainCategoryWithQuestionCount(
                            selectedCategoryId,
                            userId
                        );
                    setSubCategories((prev) => ({
                        ...prev,
                        [selectedCategoryId]: response.data.subcategories,
                    }));
                } catch (error) {
                    console.error("Error fetching subcategories:", error);
                }
            };
            fetchSubcategories();
        }
    }, [selectedCategoryId, userId]);
    
    // Fetch *all* subcategories for the Reviewed/Uploaded table filters dropdowns
    useEffect(() => {
        async function fetchAllSubcategories() {
            try {
                if (universityName && courseName) {
                    const response = await getSubcategories(universityName, courseName);
                    setAllSubcategories(response || []);
                } else {
                    console.error("Missing universityName or courseName:", {
                        universityName,
                        courseName,
                    });
                }
            } catch (error) {
                console.error("Error fetching subcategories:", error.message);
            }
        }

        fetchAllSubcategories();
    }, [universityName, courseName]);

    // Fetch categories and subcategories for the table view
    useEffect(() => {
        async function fetchData() {
            try {
                const categoriesData = await getCategories(universityName);
                setCategories(categoriesData);
            } catch (error) {
                console.error("Error fetching table data:", error.message);
            }
        }

        if (isTableViewMode && universityName) {
            fetchData();
        }
    }, [isTableViewMode, universityName]);


    const fetchUploadedQuestions = useCallback(async () => {
        try {
            const data = await getUploadedQuestions({
                ...uploadFilters,
                uploaded_by: userId,
                is_mock_bank: isMockBankMode ? "true" : "false",
            });
            setUploadedQuestions(data);
        } catch (error) {
            console.error("Failed to fetch uploaded questions:", error);
        }
    }, [uploadFilters, userId, isMockBankMode]);

    useEffect(() => {
        setUploadFilters((prev) => ({
            ...prev,
            is_mock_bank: isMockBankMode ? "true" : "false",
        }));
    }, [isMockBankMode]);

    // Fetch uploaded questions for the table
    useEffect(() => {
        if (showUploadedTable) {
            fetchUploadedQuestions();
        }
    }, [uploadFilters, showUploadedTable, fetchUploadedQuestions]);

    // Fetch reviewed questions for the "Reviewed Questions" tab
    useEffect(() => {
        async function fetchReviewedQuestions() {
            try {
                if (activeTab === "reviewed") {
                    setLoadingReviewedQuestions(true);

                    // Fetch reviewed questions
                    const reviewedResponse = await getReviewedQuestions(
                        userId,
                        isMockBankMode
                    );
                    const reviewedData = reviewedResponse.reviewedQuestions || [];

                    setReviewedQuestions(reviewedData);

                    // Initialize `editedQuestions` state based on reviewed questions
                    const initialEditedState = {};
                    reviewedData.forEach((question) => {
                        // Determine correct_choice_index for the editable field
                        const correctChoice = question.choices?.find(c => c.is_correct);
                        const correctIndex = correctChoice 
                            ? question.choices.findIndex(c => c.is_correct) + 1 
                            : "";

                        initialEditedState[question.question_id] = {
                            question_text: question.question_text,
                            lecture_reference: question.lecture_reference,
                            type: question.type,
                            high_yield: question.high_yield,
                            image: question.image,
                            choices: question.choices || [],
                            subcat_id: question.subcat_id,
                            correct_choice_index: correctIndex, // Initialize with 1-based index
                            child_question_ids: Array.isArray(question.child_question_ids)
                                ? question.child_question_ids
                                : [], // <-- SAQ parts here
                        };
                    });
                    setEditedQuestions(initialEditedState);
                }
            } catch (error) {
                console.error("Error fetching reviewed questions:", error);
            } finally {
                setLoadingReviewedQuestions(false);
            }
        }

        if (universityName && courseName) {
            fetchReviewedQuestions();
        }
    }, [universityName, courseName, activeTab, userId, isMockBankMode]);


    // --- UI/Action Handlers ---

    const handleTargetChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= 0) {
            setTargetCount(value);
        }
    };

    // Function to handle category change (used for upload form and add subcategory)
    const handleCategoryChange = (event) => {
        setSelectedCategoryId(event.target.value);
        setSelectedSubcategoryId(""); // Reset subcategory selection
    };

    // Function to handle subcategory change (used for upload form)
    const handleSubcategoryChange = (event) => {
        setSelectedSubcategoryId(event.target.value);
    };

    // Handlers for Uploaded Questions Filters
    const handleCategoryFilterChange = (event) => {
        const selectedId = event.target.value;
        setUploadFilters((prev) => ({
            ...prev,
            cat_id: selectedId,
            subcat_id: "", // reset subcat
        }));
    };

    const handleSubcategoryFilterChange = (event) => {
        const selectedId = event.target.value;
        setUploadFilters((prev) => ({
            ...prev,
            subcat_id: selectedId,
        }));
    };

    // Handlers for adding categories and subcategories
    const handleAddCategory = async () => {
        if (!validateFields({ categoryName, categoryYear })) {
            alert("Please fill in all required fields for the category.");
            return;
        }

        try {
            await addCategory({
                cat_name: categoryName,
                university_name: universityName,
                course_name: courseName,
                year: categoryYear,
            });
            alert("Category added successfully! Refresh the page to see updates.");
            setCategoryName("");
            setCategoryYear("");
        } catch (error) {
            console.error("Error adding category:", error.message);
        }
    };

    const handleAddSubcategory = async () => {
        if (!validateFields({ selectedCategoryId, subcategoryName })) {
            alert("Please fill in all required fields for the subcategory.");
            return;
        }

        try {
            await addSubcategory({
                cat_id: selectedCategoryId,
                subcategory_name: subcategoryName,
                course_name: courseName,
                university_name: universityName,
            });
            alert("Subcategory added successfully!");
            setSubcategoryName("");
        } catch (error) {
            console.error("Error adding subcategory:", error.message);
        }
    };


    // Handler for uploading questions and choices (Consolidated)
    const handleUpload = async () => {
        if (
            !validateFields({
                selectedSubcategoryId,
                questionText,
                lectureReference,
                questionType,
                choicesText,
                explanationsText,
                correctChoiceIndex,
            })
        ) {
            alert("Please fill in all required fields.");
            return;
        }

        // Parse choices & explanations for Part A
        const parsedChoices = parseInput(choicesText);
        const parsedExplanations = parseInput(explanationsText);
        if (parsedChoices.length !== parsedExplanations.length) {
            alert("Each choice must have a corresponding explanation.");
            return;
        }
        const correctIndex = parseInt(correctChoiceIndex, 10);
        if (
            isNaN(correctIndex) ||
            correctIndex < 1 ||
            correctIndex > parsedChoices.length
        ) {
            alert(
                "Correct choice index must be a valid number between 1 and the number of choices."
            );
            return;
        }

        let childJson = null;
        if (questionType === "3") {
            if (
                parsedChoices.length !== 1 ||
                parsedExplanations.length !== 1 ||
                correctIndex !== 1
            ) {
                alert(
                    "For SAQ (type 3), Part A must have exactly ONE choice, ONE explanation, and Correct Index = 1."
                );
                return;
            }
            if (saqParts.length === 0) {
                alert("Add at least one SAQ sub-question (Part B).");
                return;
            }
            const invalid = saqParts.some(
                (p) => !p.question_text?.trim() || !p.correct_answer?.trim()
            );
            if (invalid) {
                alert("Each SAQ part needs text and a correct answer.");
                return;
            }
            childJson = buildChildJson();
        }

        try {
            // --- Build FormData for question and image upload ---
            const fd = new FormData();
            fd.append("subcat_id", selectedSubcategoryId);
            fd.append("question_text", questionText);
            fd.append("lecture_reference", lectureReference);
            fd.append("type", questionType);
            fd.append("high_yield", String(highYield));
            fd.append("is_mock_bank", String(isMockBankMode));
            if (isMockBankMode && portalMockName.trim()) {
                fd.append("portal_mock_name", portalMockName.trim());
            }

            if (mainImageFile) {
                fd.append("image", mainImageFile);
            }
            
            if (questionType === "3" && childJson) {
                fd.append("child_question_ids", JSON.stringify(childJson));

                // Append only the SAQ part files that exist, in ascending part index order
                const positions = Object.keys(saqImageFiles)
                    .map(Number)
                    .filter((n) => !Number.isNaN(n))
                    .sort((a, b) => a - b);
                positions.forEach((idx) => {
                    const file = saqImageFiles[idx];
                    if (file) fd.append("saq_images", file);
                });
                fd.append("saq_image_positions", JSON.stringify(positions));
            }

            // POST the question
            const questionResponse = await uploadQuestion(fd);

            if (questionResponse.success) {
                const questionId = questionResponse.question.question_id;

                // Upload the choices
                for (let i = 0; i < parsedChoices.length; i++) {
                    const choiceData = {
                        question_id: questionId,
                        option_text: parsedChoices[i],
                        explanation_text: parsedExplanations[i],
                        is_correct: i === correctIndex - 1, // Correct index is 1-based
                        uploaded_by: userId,
                    };
                    await uploadChoice(choiceData);
                }

                alert("Question uploaded successfully!");
                resetForm();
            } else {
                alert("Failed to upload question: " + questionResponse.error);
            }
        } catch (error) {
            console.error("Error uploading question:", error.message);
            alert("An error occurred while uploading the question.");
        }
    };

    // --- Reviewed-tab SAQ helpers ---
    const findQKey = (obj = {}) =>
        Object.keys(obj).find(
            (k) => k.startsWith("question_") && k.endsWith("_text")
        );

    const nextLetterFromChild = (arr = []) =>
        String.fromCharCode("B".charCodeAt(0) + arr.length);

    const addReviewedSaqPart = (qid) => {
        setEditedQuestions((prev) => {
            const current = prev[qid] || {};
            const list = Array.isArray(current.child_question_ids)
                ? [...current.child_question_ids]
                : [];
            const letter = nextLetterFromChild(list);
            const newObj = {
                [`question_${letter.toLowerCase()}_text`]: "",
                category: "",
                category_id: "", 
                subcategory: "",
                subcategory_id: "", 
                image: "",
                university_name: universityName,
                course_name: courseName,
                correct_answer: "",
            };
            return {
                ...prev,
                [qid]: {
                    ...current,
                    child_question_ids: [...list, newObj],
                },
            };
        });
    };

    const updateReviewedSaqPart = (qid, idx, field, value) => {
        setEditedQuestions((prev) => {
            const current = prev[qid] || {};
            const list = Array.isArray(current.child_question_ids)
                ? [...current.child_question_ids]
                : [];
            const obj = { ...(list[idx] || {}) };

            if (field === "question_text") {
                const qKey = findQKey(obj) || `question_${nextLetterFromChild(list).toLowerCase()}_text`; 
                obj[qKey] = value;
            } else {
                obj[field] = value;
            }
            list[idx] = obj;

            return {
                ...prev,
                [qid]: { ...current, child_question_ids: list },
            };
        });
    };

    const removeReviewedSaqPart = (qid, idx) => {
        setEditedQuestions((prev) => {
            const current = prev[qid] || {};
            const list = Array.isArray(current.child_question_ids)
                ? [...current.child_question_ids]
                : [];
            list.splice(idx, 1);
            return {
                ...prev,
                [qid]: { ...current, child_question_ids: list },
            };
        });
    };
    
    // Handler for saving reviewed questions (Consolidated)
    const handleSave = async (questionId) => {
        const questionData = { ...editedQuestions[questionId] };

        // Only compute correct index for types that actually use choices and when choices exist
        const typeStr = String(questionData.type);
        if (
            (typeStr === "1" || typeStr === "3") &&
            Array.isArray(questionData.choices) &&
            questionData.choices.length > 0
        ) {
            const correctIndex = parseInt(questionData.correct_choice_index, 10) - 1;
            if (
                isNaN(correctIndex) ||
                correctIndex < 0 ||
                correctIndex >= questionData.choices.length
            ) {
                alert("Invalid correct choice index. Please check choices.");
                return;
            }
            questionData.choices = questionData.choices.map((choice, index) => ({
                ...choice,
                is_correct: index === correctIndex,
            }));
            // Remove the temporary state field before sending
            delete questionData.correct_choice_index;
        } else {
            // Not using choices; don't send a junk correct index
            delete questionData.correct_choice_index;
        }

        try {
            const response = await updateQuestion({
                question_id: questionId,
                subcat_id: questionData.subcat_id, // Include subcat_id
                ...questionData, // <-- includes choices and child_question_ids
            });

            if (response.success) {
                alert("Question updated successfully! It will no longer appear here.");
                setReviewedQuestions((prev) =>
                    prev.filter((q) => q.question_id !== questionId)
                );
                setEditedQuestions((prev) => {
                    const updated = { ...prev };
                    delete updated[questionId];
                    return updated;
                });
            } else {
                alert("Failed to update question: " + response.error);
            }
        } catch (error) {
            console.error("Error saving question:", error);
            alert("An error occurred while saving the question.");
        }
    };


    // --- JSX Rendering ---

    return (
        <Box p={3}>
            <AdminBankModeTabs />
            <Typography variant="h4" gutterBottom>
                {isTableViewMode
                    ? "View Categories and Subcategories"
                    : isCategoryMode
                        ? "Manage Categories & Subcategories"
                        : activeTab === "upload"
                            ? isMockBankMode
                                ? "Upload Question (Mock bank)"
                                : "Upload Question"
                            : isMockBankMode
                                ? "Reviewed Questions (Mock bank)"
                                : "Reviewed Questions"}
            </Typography>

            {/* Toggle Buttons */}
            <Box mb={2}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setIsCategoryMode((prev) => !prev)}
                    disabled={isTableViewMode}
                >
                    {isCategoryMode
                        ? "Switch to Question Upload"
                        : "Switch to Manage Categories"}
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setIsTableViewMode((prev) => !prev)}
                    style={{ marginLeft: "10px" }}
                >
                    {isTableViewMode
                        ? "Hide Tables"
                        : "View Categories and Subcategories"}
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setActiveTab("upload")}
                    style={{ marginLeft: "10px" }}
                    disabled={activeTab === 'upload'}
                >
                    Upload Question
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setActiveTab("reviewed")}
                    style={{ marginLeft: "10px" }}
                    disabled={activeTab === 'reviewed'}
                >
                    Reviewed Questions
                </Button>
            </Box>

            {/* Content Areas */}
            {isTableViewMode ? (
                <Box>
                    <Typography variant="h5">Categories</Typography>
                    <TableContainer component={Paper} style={{ marginTop: "16px" }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Category ID</TableCell>
                                    <TableCell>Category Name</TableCell>
                                    <TableCell>University Name</TableCell>
                                    <TableCell>Course Name</TableCell>
                                    <TableCell>Year</TableCell>
                                    <TableCell>Subcategories</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categories.map((category) => (
                                    <TableRow key={category.cat_id}>
                                        <TableCell>{category.cat_id}</TableCell>
                                        <TableCell>{category.cat_name}</TableCell>
                                        <TableCell>{category.university_name}</TableCell>
                                        <TableCell>{category.course_name}</TableCell>
                                        <TableCell>{category.year}</TableCell>
                                        <TableCell>
                                            {category.subcategories.map((subcat) => (
                                                <div key={subcat.subcat_id}>
                                                    {subcat.subcategory_name} (ID: {subcat.subcat_id})
                                                </div>
                                            ))}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ) : isCategoryMode ? (
                <Box mt={4}>
                    <Typography variant="h5">Add Category</Typography>
                    <TextField
                        label="Category Name"
                        fullWidth
                        margin="normal"
                        value={categoryName}
                        onChange={(e) => setCategoryName(e.target.value)}
                    />
                    <TextField
                        label="Year"
                        type="number"
                        fullWidth
                        margin="normal"
                        value={categoryYear}
                        onChange={(e) => setCategoryYear(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddCategory}
                    >
                        Add Category
                    </Button>

                    <Typography variant="h5" mt={4}>
                        Add Subcategory
                    </Typography>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Parent Category</InputLabel>
                        <Select value={selectedCategoryId} onChange={handleCategoryChange}>
                            {mainCategories.map((cat) => (
                                <MenuItem key={cat.cat_id} value={cat.cat_id}>
                                    {cat.cat_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Subcategory Name"
                        fullWidth
                        margin="normal"
                        value={subcategoryName}
                        onChange={(e) => setSubcategoryName(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddSubcategory}
                    >
                        Add Subcategory
                    </Button>
                </Box>
            ) : activeTab === "upload" ? (
                <Box mt={4}>
                    {/* Progress */}
                    <Box mb={3}>
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <>
                                <Typography variant="h6">
                                    Confirmed Questions Uploaded: {confirmedCount}
                                </Typography>

                                <Typography variant="h6">
                                    If you're getting errors, your cookie might have expired
                                    (every 4 hours). Log back out and then sign in again.
                                </Typography>
                                <TextField
                                    label="Target Questions"
                                    type="number"
                                    value={targetCount}
                                    onChange={handleTargetChange}
                                    fullWidth
                                    margin="normal"
                                />
                                <Typography variant="body1">
                                    Progress: {((confirmedCount / targetCount) * 100).toFixed(2)}%
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Category Selection */}
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Main Category</InputLabel>
                        <Select
                            value={selectedCategoryId}
                            onChange={handleCategoryChange}
                        >
                            {mainCategories.map((category) => (
                                <MenuItem key={category.cat_id} value={category.cat_id}>
                                    {category.cat_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal" disabled={!selectedCategoryId}>
                        <InputLabel>Subcategory</InputLabel>
                        <Select
                            value={selectedSubcategoryId}
                            onChange={handleSubcategoryChange}
                        >
                            {(subCategories[selectedCategoryId] || []).map((subcat) => (
                                <MenuItem key={subcat.subcat_id} value={subcat.subcat_id}>
                                    {subcat.subcategory_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Question Text */}
                    <TextField
                        label="Question Text"
                        multiline
                        rows={4}
                        fullWidth
                        margin="normal"
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                    />
                    <TextField
                        label="Lecture Reference"
                        fullWidth
                        margin="normal"
                        value={lectureReference}
                        onChange={(e) => setLectureReference(e.target.value)}
                    />

                    {isMockBankMode && (
                        <TextField
                            label="Mock pack name (mock_name when released)"
                            fullWidth
                            margin="normal"
                            value={portalMockName}
                            onChange={(e) => setPortalMockName(e.target.value)}
                            helperText="e.g. current, portal_pool, or a named exam. Used when an admin releases staged mock rows."
                        />
                    )}

                    {/* Question Type */}
                    <Typography variant="h6" gutterBottom mt={4}>
                        Types of Questions:
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        1 = MCQ
                        <br />2 = VSAQ
                        <br />3 = SAQ (Part A + sub-questions B/C/D…)
                    </Typography>

                    <TextField
                        label="Question Type"
                        fullWidth
                        margin="normal"
                        value={questionType}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (v === "1" || v === "2" || v === "3" || v === "")
                                setQuestionType(v);
                            else alert("Only 1 (MCQ), 2 (VSAQ) or 3 (SAQ) are allowed.");
                        }}
                        helperText="Enter 1 for MCQ, 2 for VSAQ, 3 for SAQ."
                    />
                    
                    {/* Main Image Upload Section */}
                    <Typography variant="h6" mt={4}>
                        Main Image (optional)
                    </Typography>
                    <Box mt={1}>
                        <input
                            id="main-image-input"
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) setMainImageFile(f);
                            }}
                        />
                        <label htmlFor="main-image-input">
                            <Button variant="outlined" component="span" color="secondary">
                                Select Main Image
                            </Button>
                        </label>
                        {mainImageFile && (
                            <Typography
                                variant="body2"
                                sx={{ ml: 2, display: "inline-block" }}
                            >
                                Selected: {mainImageFile.name}
                            </Typography>
                        )}
                    </Box>

                    {/* Choices Section */}
                    <Typography variant="h6" mt={4}>
                        Choices (Separate each choice with a backslash `\`)
                    </Typography>
                    <TextField
                        label="Choices Text"
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        value={choicesText}
                        onChange={(e) => setChoicesText(e.target.value)}
                        helperText="Example: Choice 1 \ Choice 2 \ Choice 3"
                    />
                    <Typography variant="h6" mt={4}>
                        Explanations (Separate each explanation with a backslash `\`)
                    </Typography>
                    <TextField
                        label="Explanations Text"
                        multiline
                        rows={3}
                        fullWidth
                        margin="normal"
                        value={explanationsText}
                        onChange={(e) => setExplanationsText(e.target.value)}
                        helperText="Example: Explanation 1 \ Explanation 2 \ Explanation 3"
                    />
                    <TextField
                        label="Correct Choice Index (1-based)"
                        type="number"
                        fullWidth
                        margin="normal"
                        value={correctChoiceIndex}
                        onChange={(e) => setCorrectChoiceIndex(e.target.value)}
                        helperText="Enter the index (1-based) of the correct choice."
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={highYield}
                                onChange={(e) => setHighYield(e.target.checked)}
                            />
                        }
                        label="High Yield"
                    />

                    {/* SAQ Sub-questions Section (for Upload form) */}
                    {questionType === "3" && (
                        <Box
                            mt={4}
                            sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2 }}
                        >
                            <Typography variant="h6" gutterBottom>
                                SAQ Sub-questions (Parts B/C/D…)
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Add Part B and onwards. Each part needs the sub-question text
                                and a model answer. Image is optional. Category/Subcategory/Uni/Course auto-fill and are stored per part.
                            </Typography>

                            {saqParts.map((part, idx) => (
                                <Box
                                    key={idx}
                                    sx={{ p: 2, mb: 2, background: "#fafafa", borderRadius: 1 }}
                                >
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                        Part {part.letter}
                                    </Typography>
                                    <TextField
                                        label={`Question Part ${part.letter} Text`}
                                        multiline
                                        rows={3}
                                        fullWidth
                                        margin="dense"
                                        value={part.question_text}
                                        onChange={(e) =>
                                            updateSaqPart(idx, "question_text", e.target.value)
                                        }
                                    />
                                    <TextField
                                        label="Correct Answer"
                                        multiline
                                        rows={4}
                                        fullWidth
                                        margin="dense"
                                        value={part.correct_answer}
                                        onChange={(e) =>
                                            updateSaqPart(idx, "correct_answer", e.target.value)
                                        }
                                        helperText="Enter the expected answer; reviewers will see this."
                                    />
                                    {/* File picker for this SAQ part (optional) */}
                                    <Box
                                        sx={{
                                            mt: 1,
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                        }}
                                    >
                                        <input
                                            id={`saq-img-${idx}`}
                                            type="file"
                                            accept="image/*"
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                const f = e.target.files?.[0];
                                                setSaqImageFiles((prev) => ({ ...prev, [idx]: f }));
                                            }}
                                        />
                                        <label htmlFor={`saq-img-${idx}`}>
                                            <Button variant="outlined" component="span">
                                                Select image for Part {part.letter}
                                            </Button>
                                        </label>
                                        {saqImageFiles[idx] && (
                                            <Typography variant="caption">
                                                Selected: {saqImageFiles[idx].name}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={() => removeSaqPart(idx)}
                                        >
                                            Remove Part {part.letter}
                                        </Button>
                                    </Box>
                                </Box>
                            ))}

                            <Button variant="outlined" onClick={addSaqPart}>
                                + Add part {nextLetter(saqParts.length)}
                            </Button>
                        </Box>
                    )}

                    <Button variant="contained" color="primary" onClick={handleUpload} sx={{ mt: 3 }}>
                        Upload
                    </Button>
                    
                    {/* Toggle for Uploaded Questions Table */}
                    <Box mt={4}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                setShowUploadedTable((prev) => !prev);
                            }}
                        >
                            {showUploadedTable
                                ? "Hide Uploaded Questions"
                                : "Show Uploaded Questions"}
                        </Button>
                    </Box>

                    {/* Uploaded Questions Table */}
                    {showUploadedTable && (
                        <Box mt={3}>
                            <Typography variant="h5" gutterBottom>
                                Your Uploaded Questions
                            </Typography>

                            {/* Filters */}
                            <Grid container spacing={2}>
                                {/* Type */}
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={uploadFilters.type}
                                            onChange={(e) =>
                                                setUploadFilters((prev) => ({
                                                    ...prev,
                                                    type: e.target.value,
                                                }))
                                            }
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="1">MCQ</MenuItem>
                                            <MenuItem value="2">VSAQ</MenuItem>
                                            <MenuItem value="3">SAQ</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* High Yield */}
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>High Yield</InputLabel>
                                        <Select
                                            value={uploadFilters.high_yield}
                                            onChange={(e) =>
                                                setUploadFilters((prev) => ({
                                                    ...prev,
                                                    high_yield: e.target.value,
                                                }))
                                            }
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="true">Yes</MenuItem>
                                            <MenuItem value="false">No</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Confirmed */}
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Confirmed</InputLabel>
                                        <Select
                                            value={uploadFilters.is_confirmed}
                                            onChange={(e) =>
                                                setUploadFilters((prev) => ({
                                                    ...prev,
                                                    is_confirmed: e.target.value,
                                                }))
                                            }
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="true">Confirmed</MenuItem>
                                            <MenuItem value="false">Unconfirmed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {/* Category + Subcategory Filters */}
                            <Box
                                mt={3}
                                p={2}
                                sx={{ border: "1px solid #ccc", borderRadius: "8px" }}
                            >
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Main Category</InputLabel>
                                            <Select
                                                value={uploadFilters.cat_id}
                                                onChange={handleCategoryFilterChange}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {mainCategories.map((cat) => (
                                                    <MenuItem key={cat.cat_id} value={cat.cat_id}>
                                                        {cat.cat_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth disabled={!uploadFilters.cat_id}>
                                            <InputLabel>Subcategory</InputLabel>
                                            <Select
                                                value={uploadFilters.subcat_id}
                                                onChange={handleSubcategoryFilterChange}
                                            >
                                                <MenuItem value="">All</MenuItem>
                                                {(subCategories[uploadFilters.cat_id] || []).map(
                                                    (subcat) => (
                                                        <MenuItem
                                                            key={subcat.subcat_id}
                                                            value={subcat.subcat_id}
                                                        >
                                                            {subcat.subcategory_name}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Box mt={2}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={fetchUploadedQuestions}
                                    >
                                        Apply Filters
                                    </Button>
                                </Box>
                            </Box>
                            
                            {/* Uploaded Questions Table */}
                            <TableContainer component={Paper} style={{ marginTop: "16px" }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Question Text</TableCell>
                                            <TableCell>Course</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>High Yield</TableCell>
                                            <TableCell>Confirmed</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {uploadedQuestions.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6}>
                                                    No uploaded questions found.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            uploadedQuestions.map((q) => (
                                                <TableRow key={q.question_id}>
                                                    <TableCell>{q.question_id}</TableCell>
                                                    <TableCell
                                                        style={{
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {q.question_text}
                                                    </TableCell>
                                                    <TableCell>{courseName}</TableCell>
                                                    <TableCell>
                                                        {q.type === 1
                                                            ? "MCQ"
                                                            : q.type === 2
                                                                ? "VSAQ"
                                                                : q.type === 3
                                                                    ? "SAQ"
                                                                    : "Unknown"}
                                                    </TableCell>
                                                    <TableCell>{q.high_yield ? "Yes" : "No"}</TableCell>
                                                    <TableCell>{q.is_confirmed ? "Yes" : "No"}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Box>
            ) : ( /* activeTab === "reviewed" */
                <Box>
                    {loadingReviewedQuestions ? (
                        <Typography>Loading reviewed questions...</Typography>
                    ) : reviewedQuestions.length === 0 ? (
                        <Typography>No reviewed questions available.</Typography>
                    ) : (
                        reviewedQuestions.map((question) => (
                            <Box
                                key={question.question_id}
                                mb={4}
                                sx={{
                                    border: "1px solid #e0e0e0",
                                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                                    backgroundColor: "#f9f9f9",
                                }}
                            >
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6">Question ID: {question.question_id}</Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            Original Category: {question.subcategory_name} (ID: {question.subcat_id})
                                        </Typography>

                                        {/* Feedback History */}
                                        {Array.isArray(question.feedback_history) && question.feedback_history.length > 0 && (
                                            <Box mb={3}>
                                                <Typography variant="h6" sx={{ borderBottom: '1px solid #ccc', pb: 1 }}>Feedback History</Typography>
                                                {question.feedback_history.map((feedback, index) => (
                                                    <Box
                                                        key={index}
                                                        mb={2}
                                                        p={2}
                                                        sx={{
                                                            border: "1px solid #ddd",
                                                            borderRadius: "4px",
                                                            backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#ffffff",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight="bold"
                                                            sx={{ marginBottom: "4px", color: "#333" }}
                                                        >
                                                            Version {feedback.feedback_version} (Reviewer ID:{" "}
                                                            {feedback.reviewer_id})
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            Reviewer's Comment: {feedback.feedback_text}
                                                        </Typography>
                                                        <Typography variant="caption" display="block">
                                                            Submitted on:{" "}
                                                            {new Date(feedback.created_at).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        
                                        <Typography variant="h6" sx={{ borderBottom: '1px solid #ccc', pb: 1, mt: 3 }}>Editable Fields</Typography>
                                        
                                        {/* Subcategory Picker */}
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel>Subcategory (Corrected)</InputLabel>
                                            <Select
                                                value={editedQuestions[question.question_id]?.subcat_id || ""}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        question.question_id,
                                                        "subcat_id",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {allSubcategories.map((subcat) => (
                                                    <MenuItem
                                                        key={subcat.subcat_id}
                                                        value={subcat.subcat_id}
                                                    >
                                                        {subcat.subcategory_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        
                                        {/* Question Text */}
                                        <TextField
                                            label="Question Text"
                                            multiline
                                            rows={4}
                                            fullWidth
                                            margin="normal"
                                            value={editedQuestions[question.question_id]?.question_text || ""}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    question.question_id,
                                                    "question_text",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        
                                        {/* Lecture Reference */}
                                        <TextField
                                            label="Lecture Reference"
                                            fullWidth
                                            margin="normal"
                                            value={editedQuestions[question.question_id]?.lecture_reference || ""}
                                            onChange={(e) =>
                                                handleInputChange(
                                                    question.question_id,
                                                    "lecture_reference",
                                                    e.target.value
                                                )
                                            }
                                        />
                                        
                                        {/* Question Type */}
                                        <TextField
                                            label="Question Type (1 = MCQ, 2 = VSAQ, 3 = SAQ)"
                                            fullWidth
                                            margin="normal"
                                            value={editedQuestions[question.question_id]?.type || ""}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (["1", "2", "3"].includes(value) || value === "") {
                                                    handleInputChange(
                                                        question.question_id,
                                                        "type",
                                                        value
                                                    );
                                                } else {
                                                    alert(
                                                        "Invalid type. Only 1 (MCQ), 2 (VSAQ) or 3 (SAQ) are allowed."
                                                    );
                                                }
                                            }}
                                        />
                                        
                                        {/* High Yield Checkbox */}
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={editedQuestions[question.question_id]?.high_yield || false}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            question.question_id,
                                                            "high_yield",
                                                            e.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="High Yield"
                                            sx={{ mt: 1 }}
                                        />

                                        {/* Image URL */}
                                        <TextField
                                            label="Image URL (Read-only, please re-upload in upload tab if needed)"
                                            fullWidth
                                            margin="normal"
                                            value={editedQuestions[question.question_id]?.image || ""}
                                            InputProps={{ readOnly: true }} 
                                        />

                                        {/* Choices/Answers Section (for MCQ/SAQ Part A) */}
                                        <Typography variant="h6" mt={2}>
                                            Choices / Answer (Part A for SAQ)
                                        </Typography>
                                        
                                        {/* Correct Choice Index Field */}
                                        {(String(editedQuestions[question.question_id]?.type) === "1" || String(editedQuestions[question.question_id]?.type) === "3") && (
                                            <TextField
                                                label="Correct Choice Index (1-based)"
                                                type="number"
                                                fullWidth
                                                margin="normal"
                                                value={editedQuestions[question.question_id]?.correct_choice_index || ""}
                                                onChange={(e) =>
                                                    handleInputChange(
                                                        question.question_id,
                                                        "correct_choice_index",
                                                        e.target.value
                                                    )
                                                }
                                            />
                                        )}

                                        {/* Individual Choices/Explanations */}
                                        {editedQuestions[question.question_id]?.choices.map(
                                            (choice, index) => (
                                                <Box
                                                    key={choice.option_id || `new-${index}`}
                                                    p={2}
                                                    sx={{
                                                        backgroundColor: choice.is_correct ? "#e8f5e9" : "#ffebee",
                                                        borderRadius: "4px",
                                                        marginBottom: "8px",
                                                    }}
                                                >
                                                    <TextField
                                                        label={`Choice ${index + 1}`}
                                                        fullWidth
                                                        margin="normal"
                                                        value={choice.option_text || ""}
                                                        onChange={(e) =>
                                                            handleChoiceChange(
                                                                question.question_id,
                                                                index,
                                                                "option_text",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                    <TextField
                                                        label="Explanation"
                                                        multiline
                                                        rows={2}
                                                        fullWidth
                                                        margin="normal"
                                                        value={choice.explanation_text || ""}
                                                        onChange={(e) =>
                                                            handleChoiceChange(
                                                                question.question_id,
                                                                index,
                                                                "explanation_text",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </Box>
                                            )
                                        )}

                                        {/* SAQ editor for Reviewed (only if type === 3) */}
                                        {String(editedQuestions[question.question_id]?.type) === "3" && (
                                            <Box
                                                sx={{
                                                    mt: 3,
                                                    p: 2,
                                                    border: "1px solid #ddd",
                                                    borderRadius: 1,
                                                    background: "#fafafa",
                                                }}
                                            >
                                                <Typography variant="h6" gutterBottom>
                                                    SAQ Sub-questions (Parts B/C/D…)
                                                </Typography>

                                                {Array.isArray(editedQuestions[question.question_id]?.child_question_ids) &&
                                                    editedQuestions[question.question_id].child_question_ids.length > 0 ? (
                                                    editedQuestions[question.question_id].child_question_ids.map((part, idx) => {
                                                        const qKey = findQKey(part);
                                                        const letter = qKey
                                                            ? qKey.split("_")[1].toUpperCase()
                                                            : nextLetterFromChild(editedQuestions[question.question_id].child_question_ids.slice(0, idx)); // Approximate letter if key is missing
                                                        const qText = qKey ? part[qKey] : "";

                                                        return (
                                                            <Box
                                                                key={idx}
                                                                sx={{
                                                                    p: 2,
                                                                    mb: 2,
                                                                    background: "#fff",
                                                                    borderRadius: 1,
                                                                    border: "1px solid #eee",
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{ mb: 1 }}
                                                                >
                                                                    Part {letter}
                                                                </Typography>

                                                                <TextField
                                                                    label={`Question Part ${letter} Text`}
                                                                    multiline
                                                                    rows={3}
                                                                    fullWidth
                                                                    margin="dense"
                                                                    value={qText}
                                                                    onChange={(e) =>
                                                                        updateReviewedSaqPart(
                                                                            question.question_id,
                                                                            idx,
                                                                            "question_text",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />

                                                                <TextField
                                                                    label="Correct Answer"
                                                                    multiline
                                                                    rows={4}
                                                                    fullWidth
                                                                    margin="dense"
                                                                    value={part.correct_answer || ""}
                                                                    onChange={(e) =>
                                                                        updateReviewedSaqPart(
                                                                            question.question_id,
                                                                            idx,
                                                                            "correct_answer",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />

                                                                <TextField
                                                                    label="Image URL (optional)"
                                                                    fullWidth
                                                                    margin="dense"
                                                                    value={part.image || ""}
                                                                    onChange={(e) =>
                                                                        updateReviewedSaqPart(
                                                                            question.question_id,
                                                                            idx,
                                                                            "image",
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                />

                                                                <Box sx={{ mt: 1 }}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            removeReviewedSaqPart(
                                                                                question.question_id,
                                                                                idx
                                                                            )
                                                                        }
                                                                    >
                                                                        Remove Part {letter}
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="textSecondary"
                                                        sx={{ mb: 1 }}
                                                    >
                                                        No sub-questions yet.
                                                    </Typography>
                                                )}

                                                <Button
                                                    variant="outlined"
                                                    onClick={() =>
                                                        addReviewedSaqPart(question.question_id)
                                                    }
                                                >
                                                    + Add next part
                                                </Button>
                                            </Box>
                                        )}

                                        {/* Save Button */}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleSave(question.question_id)}
                                            sx={{ mt: 3 }}
                                        >
                                            Save Changes
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))
                    )}
                </Box>
            )}
        </Box>
    );
};

export default DashboardQuestionUpload;